import React from 'react';
import { Box, Divider, ListItem, ListItemText, Typography } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { CasesContext } from '../../../context/casesContext';
import { CurrentCohortSubscriptionContext } from '../../../context/currentCohortSubscriptionContext';
import { UserContext } from '../../../context/userContext';
import OverallRing from './OverallRing';
import TypeRow from './TypeRow';
import { AttributeLimitRow, CategoryLimitRow, categoryStatus, LimitStatus } from './LimitRow';
import {
  ProgressAttributeRule, ProgressCategoryRule, ProgressSnapshot, ProgressTypeRule,
} from './types';
import { useProgress } from './useProgress';

// Sort order: "problem" items first so healers see what's blocking them
// without scrolling. Weight assigns lower numbers = higher visual priority.
const CATEGORY_WEIGHT: Record<LimitStatus, number> = {
  AT_CAP: 0,
  CLOSE: 1,
  NEED_MORE: 2,
  OK: 3,
  MET: 4,
};

const sortCategoryRules = (rules: ProgressCategoryRule[]) =>
  [...rules].sort(
    (a, b) => CATEGORY_WEIGHT[categoryStatus(a)] - CATEGORY_WEIGHT[categoryStatus(b)]
  );

const sortAttributeRules = (rules: ProgressAttributeRule[]) =>
  [...rules].sort((a, b) => {
    if (a.is_met === b.is_met) return b.remaining - a.remaining;
    return a.is_met ? 1 : -1;
  });

// Types get their own natural order (met last so the "in progress" ones
// stay near the top).
const sortTypeRules = (rules: ProgressTypeRule[]) => {
  return [...rules].sort((a, b) => {
    if (a.is_met === b.is_met) return b.remaining - a.remaining;
    return a.is_met ? 1 : -1;
  });
};

interface ProgressDashboardProps {
  courseNameLabel: string;
}

const HeaderLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Typography
    variant='overline'
    sx={{
      color: 'rgba(255,255,255,0.7)',
      fontSize: 10,
      fontWeight: 700,
      letterSpacing: 0.8,
      px: 3,
      pt: 1.25,
      pb: 0.5,
      display: 'block',
    }}
  >
    {children}
  </Typography>
);

// Indented, quieter label for a group nested under a HeaderLabel.
const SubHeaderLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Typography
    sx={{
      color: 'rgba(255,255,255,0.4)',
      fontSize: 10,
      fontWeight: 600,
      letterSpacing: 0.4,
      pl: 4,
      pr: 3,
      pt: 1,
      pb: 0.5,
      display: 'block',
    }}
  >
    {children}
  </Typography>
);

const RuleFootnote: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Box sx={{ display: 'flex', gap: 0.75, px: 3, pt: 0.75, pb: 0.5 }}>
    <InfoOutlinedIcon sx={{ fontSize: 13, color: 'rgba(255,255,255,0.35)', mt: '1px' }} />
    <Typography sx={{ color: 'rgba(255,255,255,0.45)', fontSize: 10.5, lineHeight: 1.45 }}>
      {children}
    </Typography>
  </Box>
);

const ZeroState: React.FC<{ message: string }> = ({ message }) => (
  <Box sx={{ px: 3, py: 2 }}>
    <Typography sx={{ color: 'rgba(255,255,255,0.6)', fontSize: 12, lineHeight: 1.5 }}>
      {message}
    </Typography>
  </Box>
);

// The hook keeps the last good snapshot when a refresh fails, so without this
// the panel would go on presenting stale figures as current.
const StaleNotice: React.FC = () => (
  <Box sx={{ display: 'flex', gap: 0.75, px: 3, pt: 0.75, pb: 0.5 }}>
    <InfoOutlinedIcon sx={{ fontSize: 13, color: 'warning.light', mt: '1px' }} />
    <Typography sx={{ color: 'warning.light', fontSize: 10.5, lineHeight: 1.45 }}>
      Couldn’t refresh just now — these figures may be out of date.
    </Typography>
  </Box>
);

/**
 * World-class glanceable progress panel for the left sidebar. Replaces the
 * legacy hard-coded ProgressListItem trio. Data-driven — reflects whatever
 * rules an admin has configured for the current cohort's course.
 *
 * Refresh pulse: the useProgress hook re-runs whenever `cases` changes,
 * which is the same signal ManageCaseDialog uses via `refreshCases()`.
 */
const ProgressDashboard: React.FC<ProgressDashboardProps> = ({ courseNameLabel }) => {
  const { user } = React.useContext(UserContext);
  const { cases } = React.useContext(CasesContext);
  const { currentCohortSubscription } = React.useContext(CurrentCohortSubscriptionContext);

  // Mirrors what `access_check` permits on a GET: a healer who has completed
  // the course can still read their progress, they just cannot submit.
  const enabled = Boolean(
    user && !user.is_case_reviewer &&
    ['ACTIVE', 'COMPLETE'].includes(currentCohortSubscription?.state ?? '')
  );

  // `cases` is the pulse: any submit/unsubmit/create/delete triggers a
  // refreshCases(), which in turn changes this array reference and re-fires
  // the progress fetch below.
  const { snapshot, isLoading, error } = useProgress(enabled, cases);

  return (
    <>
      <ListItem sx={{ py: 2, px: 3 }}>
        <ListItemText sx={{ color: '#fff' }}>{courseNameLabel}</ListItemText>
      </ListItem>

      {renderBody(snapshot, isLoading, error)}
      <Box sx={{ mb: 2 }} />
    </>
  );
};

function renderBody(
  snapshot: ProgressSnapshot | null,
  isLoading: boolean,
  error: string | null,
): React.ReactNode {
  if (!snapshot && isLoading) {
    return <ZeroState message='Loading progress…' />;
  }
  if (!snapshot && error) {
    return <ZeroState message='Progress temporarily unavailable.' />;
  }
  if (!snapshot) {
    return <ZeroState message='Progress not available for this cohort.' />;
  }
  if (!snapshot.is_configured) {
    return <ZeroState message='Progress tracking has not been configured for this course yet.' />;
  }

  const typeRules = sortTypeRules(snapshot.type_rules);
  const categoryRules = sortCategoryRules(snapshot.category_rules);
  const attributeRules = sortAttributeRules(snapshot.attribute_rules);

  return (
    <>
      {error && <StaleNotice />}
      <OverallRing
        approved={snapshot.total_approved}
        required={snapshot.total_required}
        isComplete={snapshot.is_complete}
      />

      {typeRules.length > 0 && (
        <>
          <HeaderLabel>Case requirements</HeaderLabel>
          {typeRules.map((rule) => (
            <TypeRow key={rule.rule_id} rule={rule} />
          ))}
        </>
      )}

      {categoryRules.length > 0 && (
        <>
          <Divider sx={{ borderColor: 'rgba(255,255,255,0.08)', mt: 1.5, mx: 3 }} />
          <HeaderLabel>Limits that apply</HeaderLabel>
          <SubHeaderLabel>Categories</SubHeaderLabel>
          {categoryRules.map((rule) => (
            <CategoryLimitRow key={rule.rule_id} rule={rule} />
          ))}
          <RuleFootnote>
            Once a category limit is reached, further cases in it can’t be submitted
            and won’t count toward your total.
          </RuleFootnote>
        </>
      )}

      {attributeRules.length > 0 && (
        <>
          <Divider sx={{ borderColor: 'rgba(255,255,255,0.08)', mt: 1.5, mx: 3 }} />
          <HeaderLabel>Recommended</HeaderLabel>
          {attributeRules.map((rule) => (
            <AttributeLimitRow key={rule.rule_id} rule={rule} />
          ))}
          <RuleFootnote>
            Guidance based on what happened in your sessions. These targets never
            block a submission.
          </RuleFootnote>
        </>
      )}
    </>
  );
}

export default ProgressDashboard;
